import React, { useState, useEffect, useCallback } from "react";
import { Card, Typography, Flex, Button, Collapse, Form, Input, Select, Row, Col, Modal, Avatar, message, Tooltip, Spin } from "antd";
import { QuestionCircleOutlined, SendOutlined, MailOutlined, PhoneOutlined, ShareAltOutlined, CopyOutlined, UserOutlined, CheckOutlined, ExclamationCircleOutlined, MobileOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getCompanyProfile, requestRelationshipManager, submitSupportRequest } from "../../services/organizationService";
import { getCookie } from "../../utils/cookieUtils";

const { Title, Text, Paragraph } = Typography;

const FAQ_ITEMS = [
  {
    key: "1",
    label: "How does mobile call recording sync with the web CRM?",
    children: <Paragraph style={{ color: "#475569", margin: 0 }}>When your sales reps use the DealVoice Android application, all client incoming and outgoing calls are recorded locally and automatically synchronized with the web workspace in real time when an internet connection is available.</Paragraph>,
  },
  {
    key: "2",
    label: "How do I invite and register sales representatives?",
    children: (
      <Paragraph style={{ color: "#475569", margin: 0 }}>
        Share your unique <b>Device Connect Code</b> with your operators. When they install and open the DealVoice mobile app, they enter this code to link their device directly to your enterprise tenant.
      </Paragraph>
    ),
  },
  {
    key: "3",
    label: "How does subscription billing and seat pricing work?",
    children: (
      <Paragraph style={{ color: "#475569", margin: 0 }}>
        Subscription is calculated per active sales rep seat per day (₹10/seat/day) plus 18% GST. You can extend or renew your company plan anytime under the <b>Company Settings</b> tab.
      </Paragraph>
    ),
  },
];

function SupportSection() {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [companyProfile, setCompanyProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // RM State & Countdown
  const [rmRequestLoading, setRmRequestLoading] = useState(false);
  const [rmCountdown, setRmCountdown] = useState("");

  // Share Modal State
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [copiedConnectCode, setCopiedConnectCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Ticket Form State
  const [form] = Form.useForm();
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // 8-hour throttle check
  const canRequestRm = useCallback(() => {
    if (!companyProfile) return false;
    if (!companyProfile.rmRequestTime) return true;
    const hoursSinceRequest = (Date.now() - new Date(companyProfile.rmRequestTime).getTime()) / (1000 * 60 * 60);
    return hoursSinceRequest >= 8;
  }, [companyProfile]);

  // Start RM Timer Countdown
  useEffect(() => {
    if (!companyProfile?.rmRequestTime) {
      setRmCountdown("");
      return;
    }

    const interval = setInterval(() => {
      const start = new Date(companyProfile.rmRequestTime).getTime();
      const end = start + 8 * 60 * 60 * 1000;
      const now = Date.now();
      const diff = end - now;

      if (diff <= 0) {
        setRmCountdown("");
        clearInterval(interval);
      } else {
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);
        setRmCountdown(`${h}h ${m}m ${s}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [companyProfile?.rmRequestTime]);

  // Fetch Company Profile
  const fetchProfile = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setProfileLoading(true);
    try {
      const res = await getCompanyProfile(effectiveCompanyCode);
      if (res?.success && res.company) {
        setCompanyProfile(res.company);
      }
    } catch (err) {
      console.error("[SupportSection] fetchProfile error:", err);
    } finally {
      setProfileLoading(false);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Request RM Handler
  const handleRequestRm = async () => {
    if (!effectiveCompanyCode) return;
    setRmRequestLoading(true);
    try {
      const res = await requestRelationshipManager(effectiveCompanyCode);
      if (res?.success) {
        message.success(res.message || "Request submitted! Super Admin will assign an RM shortly.");
        setCompanyProfile((prev) => ({
          ...prev,
          rmRequestTime: res.rmRequestTime || new Date().toISOString(),
        }));
      } else {
        message.warning(res?.message || "Unable to request Relationship Manager at this time.");
      }
    } catch (err) {
      message.error(err.message || "Failed to submit request.");
    } finally {
      setRmRequestLoading(false);
    }
  };

  // Generate Share Message
  const getShareMessage = () => {
    const code = companyProfile?.companyCode || effectiveCompanyCode || "N/A";
    const email = companyProfile?.email || user?.email || "support@dealvoice.com";
    const mobile = companyProfile?.mobile || user?.mobile || "+91 81486 33580";

    return `Hello,\n\nTo register your employee using the DealVoice mobile app, please follow the step-by-step instructions mentioned in the link below:\nhttps://youtu.be/CiTyrb52Xuc?si=OsCNUq-aHxBKa7f4\n\nPlease use below unique device connect code to register. Your Device Connect Code is ${code}.\n\nIf you encounter any difficulties, please contact at ${email} or ${mobile}\n\nThank You.`;
  };

  // Copy Connect Code Link
  const handleCopyConnectCodeLink = () => {
    const link = "https://youtu.be/CiTyrb52Xuc?si=OsCNUq-aHxBKa7f4";
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(true);
      message.success("Setup guide link copied to clipboard!");
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  // Copy Share Message
  const handleCopyShareMessage = () => {
    navigator.clipboard.writeText(getShareMessage()).then(() => {
      message.success("Registration invitation message copied to clipboard!");
      setShareModalVisible(false);
    });
  };

  // Copy raw connect code
  const handleCopyCodeOnly = () => {
    const code = companyProfile?.companyCode || effectiveCompanyCode;
    navigator.clipboard.writeText(code).then(() => {
      setCopiedConnectCode(true);
      message.success("Device Connect Code copied!");
      setTimeout(() => setCopiedConnectCode(false), 2000);
    });
  };

  // Submit Support Ticket
  const handleSubmitTicket = async (values) => {
    setSubmittingTicket(true);
    try {
      const payload = {
        category: values.category,
        subject: values.subject,
        message: values.description,
        contactEmail: user?.email,
        contactPhone: user?.mobile,
      };

      const res = await submitSupportRequest(effectiveCompanyCode, payload);
      if (res?.success !== false) {
        form.resetFields();
        message.success("Support ticket submitted directly to DealVoice engineering!");
      } else {
        message.error(res?.message || "Failed to submit ticket.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to submit support request.");
    } finally {
      setSubmittingTicket(false);
    }
  };

  const rm = companyProfile?.relationshipManager;
  const hasRm = Boolean(rm?.name);

  return (
    <Spin spinning={profileLoading} tip="Loading support info...">
      <Flex vertical gap={24} style={{ maxWidth: 1150, margin: "0 auto" }}>
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Flex align="center" gap={12}>
            <Avatar size={42} icon={<QuestionCircleOutlined />} style={{ backgroundColor: "#eff6ff", color: "#2563eb", fontSize: "1.2rem" }} />
            <Flex vertical>
              <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                Help & Support Center
              </Title>
              <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                Device activation, dedicated account manager, and technical helpdesk
              </Text>
            </Flex>
          </Flex>

          <Button type="default" icon={<ShareAltOutlined />} onClick={() => setShareModalVisible(true)} style={{ borderRadius: 8, fontWeight: 500 }}>
            Share Registration Guide
          </Button>
        </Flex>

        {/* Main Grid: Left Column (Connect Code & RM) | Right Column (Activation Video & Guide) */}
        <Row gutter={[24, 24]}>
          {/* LEFT COLUMN */}
          <Col xs={24} lg={12}>
            <Flex vertical gap={20}>
              {/* Minimal Device Connect Code Card */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  background: "#ffffff",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
                  border: "1px solid #e2e8f0",
                }}
                styles={{ body: { padding: "24px" } }}
              >
                <Flex vertical gap={20}>
                  <Flex align="center" gap={16}>
                    <Avatar
                      size={48}
                      icon={<MobileOutlined />}
                      style={{
                        backgroundColor: "rgba(99, 102, 241, 0.08)",
                        color: "#6366f1",
                        borderRadius: 12,
                        fontSize: "1.3rem",
                      }}
                    />
                    <Flex vertical style={{ flex: 1 }}>
                      <Text
                        strong
                        style={{
                          fontSize: "0.72rem",
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        Device Connect Code
                      </Text>
                      <Flex align="center" gap={12} style={{ marginTop: 4 }}>
                        <Title
                          level={2}
                          style={{
                            margin: 0,
                            fontSize: "1.65rem",
                            fontFamily: "monospace",
                            color: "#0f172a",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {companyProfile?.companyCode || effectiveCompanyCode || "—"}
                        </Title>
                        <Tooltip title="Copy code">
                          <Button type="text" icon={copiedConnectCode ? <CheckOutlined style={{ color: "#16a34a" }} /> : <CopyOutlined />} onClick={handleCopyCodeOnly} />
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </Flex>

                  <Button
                    type="default"
                    icon={<ShareAltOutlined />}
                    onClick={() => setShareModalVisible(true)}
                    style={{
                      width: "100%",
                      borderRadius: 10,
                      fontWeight: 600,
                      height: 42,
                      fontSize: "0.88rem",
                      background: "#f8fafc",
                      borderColor: "#cbd5e1",
                    }}
                  >
                    Share Instructions with Operators
                  </Button>
                </Flex>
              </Card>

              {/* Relationship Manager Card */}
              {hasRm ? (
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 16,
                    background: "#ffffff",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
                    border: "1px solid #e2e8f0",
                  }}
                  styles={{ body: { padding: "24px" } }}
                >
                  <Flex vertical gap={16}>
                    <Flex align="center" gap={8}>
                      <UserOutlined style={{ color: "#7c3aed", fontSize: "0.9rem" }} />
                      <Text
                        strong
                        style={{
                          fontSize: "0.72rem",
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        Personal Assistance & Account Manager
                      </Text>
                    </Flex>

                    <Flex align="center" gap={14}>
                      <Avatar size={46} icon={<UserOutlined />} style={{ backgroundColor: "#7c3aed", color: "#ffffff" }} />
                      <Flex vertical>
                        <Text strong style={{ fontSize: "1.15rem", color: "#0f172a" }}>
                          {rm.name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                          Dedicated DealVoice Relationship Manager
                        </Text>
                      </Flex>
                    </Flex>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                        paddingTop: "16px",
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <div>
                        <Text style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Availability</Text>
                        <div style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 500, marginTop: 2 }}>{rm.workingDays || "Mon - Sat"}</div>
                      </div>

                      <div>
                        <Text style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Working Hours</Text>
                        <div style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 500, marginTop: 2 }}>{rm.workingHours || "9:30 AM - 6:30 PM IST"}</div>
                      </div>

                      <div>
                        <Text style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Phone Number</Text>
                        <div style={{ fontSize: "0.85rem", color: "#2563eb", fontWeight: 500, marginTop: 2 }}>
                          <a href={`tel:${rm.phone}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                            {rm.phone || "—"}
                          </a>
                        </div>
                      </div>

                      <div>
                        <Text style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Email Address</Text>
                        <div style={{ fontSize: "0.85rem", color: "#2563eb", fontWeight: 500, marginTop: 2 }}>
                          <a href={`mailto:${rm.email}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                            {rm.email || "—"}
                          </a>
                        </div>
                      </div>
                    </div>
                  </Flex>
                </Card>
              ) : (
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 16,
                    border: "1px dashed #cbd5e1",
                    background: "rgba(99, 102, 241, 0.02)",
                    textAlign: "center",
                  }}
                  styles={{ body: { padding: "28px 24px" } }}
                >
                  <Flex vertical align="center" gap={12}>
                    <Avatar size={46} icon={<UserOutlined />} style={{ backgroundColor: "#f1f5f9", color: "#6366f1" }} />
                    <Flex vertical gap={4}>
                      <Text strong style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        Need a dedicated manager for your account?
                      </Text>
                      <Text type="secondary" style={{ fontSize: "0.82rem", maxWidth: 360 }}>
                        Get assigned a dedicated Relationship Manager to assist with setup, training, and onboarding.
                      </Text>
                    </Flex>

                    <Button
                      type="primary"
                      onClick={handleRequestRm}
                      loading={rmRequestLoading}
                      disabled={!canRequestRm()}
                      style={{
                        width: "100%",
                        borderRadius: 10,
                        fontWeight: 600,
                        height: 42,
                        marginTop: 6,
                        backgroundColor: "#6366f1",
                      }}
                    >
                      {rmRequestLoading ? "Processing..." : "Request Relationship Manager"}
                    </Button>

                    {!canRequestRm() && (
                      <Flex vertical align="center" gap={4} style={{ marginTop: 8 }}>
                        <Flex align="center" gap={6}>
                          <ExclamationCircleOutlined style={{ color: "#f59e0b", fontSize: "0.9rem" }} />
                          <Text style={{ fontSize: "0.78rem", color: "#d97706", fontWeight: 600 }}>Request pending. Super Admin will assign an RM shortly.</Text>
                        </Flex>
                        {rmCountdown && (
                          <Text type="secondary" style={{ fontSize: "0.75rem", fontWeight: 500 }}>
                            Next request available in: <span style={{ color: "#6366f1", fontWeight: 700 }}>{rmCountdown}</span>
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Card>
              )}
            </Flex>
          </Col>

          {/* RIGHT COLUMN */}
          <Col xs={24} lg={12}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                background: "#ffffff",
                boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
                border: "1px solid #e2e8f0",
              }}
              styles={{ body: { padding: "24px" } }}
            >
              <Flex vertical gap={16}>
                <Flex align="center" gap={8}>
                  <VideoCameraOutlined style={{ color: "#2563eb", fontSize: "1rem" }} />
                  <Title level={5} style={{ margin: 0, color: "#0f172a" }}>
                    Activation Guide
                  </Title>
                </Flex>

                {/* 16:9 Responsive Video Container */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#f1f5f9",
                    paddingBottom: "56.25%",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <iframe
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    src="https://www.youtube.com/embed/CiTyrb52Xuc?si=SEdgGZuWNMwGjqAO"
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>

                <Text type="secondary" style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
                  Watch this 2-minute video to complete your setup. For faster deployment, you can share the link below with your operators.
                </Text>

                {/* Copy Registration Link Box */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <input
                    type="text"
                    readOnly
                    value="https://youtu.be/CiTyrb52Xuc?si=OsCNUq-aHxBKa7f4"
                    style={{
                      flex: 1,
                      border: "none",
                      background: "transparent",
                      color: "#475569",
                      fontSize: "0.8rem",
                      fontFamily: "monospace",
                      outline: "none",
                    }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    icon={copiedLink ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyConnectCodeLink}
                    style={{
                      backgroundColor: "#0f172a",
                      borderColor: "#0f172a",
                      borderRadius: 6,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {copiedLink ? "Copied" : "Copy"}
                  </Button>
                </div>
              </Flex>
            </Card>
          </Col>
        </Row>

        {/* Share Instructions Modal */}
        <Modal
          title={
            <Flex align="center" gap={8}>
              <ShareAltOutlined style={{ color: "#6366f1" }} />
              <span>Share Mobile Setup Instructions</span>
            </Flex>
          }
          open={shareModalVisible}
          onCancel={() => setShareModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setShareModalVisible(false)} style={{ borderRadius: 8 }}>
              Close
            </Button>,
            <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopyShareMessage} style={{ borderRadius: 8, backgroundColor: "#6366f1" }}>
              Copy Invitation Message
            </Button>,
          ]}
          destroyOnClose
        >
          <Flex vertical gap={12} style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: "0.82rem" }}>
              You can copy and send this message via WhatsApp, Email, or Slack to your sales operators to onboard them onto DealVoice:
            </Text>
            <div
              style={{
                background: "#f8fafc",
                padding: "16px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                fontFamily: "monospace",
                fontSize: "0.82rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                color: "#334155",
              }}
            >
              {getShareMessage()}
            </div>
          </Flex>
        </Modal>
      </Flex>
    </Spin>
  );
}

export default SupportSection;
